import React, { useState, useEffect, useCallback } from 'react';
import './NPSWidget.css';

const NPS_DISMISSED_PREFIX = 'nps_dismissed_';
const SHOW_DELAY_MS = 45000;
const MIN_SCROLL_PX = 300;

function NPSWidget({ reportUuid, onSubmitScore }) {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState('rating'); // 'rating' | 'feedback' | 'thanks'
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const dismissedKey = `${NPS_DISMISSED_PREFIX}${reportUuid}`;
    if (localStorage.getItem(dismissedKey)) return;

    let timerFired = false;
    let scrollFired = false;

    const maybeShow = () => {
      if (timerFired && scrollFired) setVisible(true);
    };

    const timer = setTimeout(() => {
      timerFired = true;
      maybeShow();
    }, SHOW_DELAY_MS);

    const onScroll = () => {
      if (window.scrollY >= MIN_SCROLL_PX) {
        scrollFired = true;
        maybeShow();
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
  }, [reportUuid]);

  const dismiss = useCallback(() => {
    setVisible(false);
    localStorage.setItem(`${NPS_DISMISSED_PREFIX}${reportUuid}`, '1');
  }, [reportUuid]);

  const handleScoreClick = (val) => {
    setScore(val);
    setStep('feedback');
  };

  const handleFeedbackSubmit = () => {
    if (submitting) return;
    setSubmitting(true);
    setStep('thanks');
    localStorage.setItem(`${NPS_DISMISSED_PREFIX}${reportUuid}`, '1');
    onSubmitScore({ score, comment: comment.trim() || undefined, reportUuid })
      .catch((err) => console.error('NPS submit failed:', err));
  };

  const handleScoreChange = (newScore) => {
    const parsed = parseInt(newScore, 10);
    if (isNaN(parsed) || parsed < 0 || parsed > 10) return;
    setScore(parsed);
  };

  if (!visible) return null;

  return (
    <div className="nps-overlay">
      <div className="nps-widget">
        <button className="nps-close" onClick={dismiss} aria-label="Dismiss">
          &times;
        </button>

        {step === 'rating' && (
          <div className="nps-content nps-rating-step">
            <h3 className="nps-question">
              How likely are you to recommend<br />us to someone you know?
            </h3>
            <div className="nps-buttons">
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((val) => (
                <button
                  key={val}
                  className="nps-score-btn"
                  onClick={() => handleScoreClick(val)}
                >
                  {val}
                </button>
              ))}
            </div>
            <div className="nps-labels">
              <span>Not at all likely</span>
              <span>Extremely Likely</span>
            </div>
          </div>
        )}

        {step === 'feedback' && (
          <div className="nps-content nps-feedback-step">
            <h3 className="nps-rated">
              You rated us a{' '}
              <select
                className="nps-score-select"
                value={score}
                onChange={(e) => handleScoreChange(e.target.value)}
              >
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((val) => (
                  <option key={val} value={val}>{val}</option>
                ))}
              </select>
            </h3>
            <div className="nps-divider" />
            <p className="nps-feedback-prompt">Will you share why?</p>
            <textarea
              className="nps-textarea"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Your feedback..."
              rows={3}
            />
            <button
              className="nps-submit-btn"
              onClick={handleFeedbackSubmit}
              disabled={submitting}
            >
              Send feedback
            </button>
          </div>
        )}

        {step === 'thanks' && (
          <div className="nps-content nps-thanks-step">
            <p className="nps-thanks-msg">Thank you for your feedback.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default NPSWidget;
